import { useState, useEffect, useRef } from 'react'
import { api } from '../utils/api'
import toast from 'react-hot-toast'

const PRICE_LABELS = { 1: '$', 2: '$$', 3: '$$$', 4: '$$$$' }

export default function RestaurantSearch({ onSelect, loading: parentLoading }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [userLocation, setUserLocation] = useState(null)
  const [mapView, setMapView] = useState(false)
  const searchTimeout = useRef(null)

  // Try to get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {} // Fall back to Richmond default
      )
    }
  }, [])

  // Load nearby restaurants on mount
  useEffect(() => {
    handleSearch('')
  }, [userLocation])

  async function handleSearch(q) {
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(async () => {
      setSearching(true)
      try {
        const params = {
          ...(q ? { query: q } : {}),
          ...(userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : {})
        }
        const result = await api.searchRestaurants(q || undefined, userLocation?.lat, userLocation?.lng)
        setResults(result.restaurants || [])
      } catch (err) {
        toast.error('Failed to search restaurants')
      } finally {
        setSearching(false)
      }
    }, 400)
  }

  function handleQueryChange(e) {
    setQuery(e.target.value)
    handleSearch(e.target.value)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <div className="p-4 pb-2">
        <input
          type="search"
          placeholder="Search restaurants in Richmond…"
          value={query}
          onChange={handleQueryChange}
          autoFocus
          className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        {userLocation && (
          <p className="text-xs text-green-600 mt-1">📍 Using your current location</p>
        )}
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {searching ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600" />
          </div>
        ) : results.length === 0 ? (
          <p className="text-center text-gray-400 py-8">
            {query ? `No restaurants found for "${query}"` : 'No restaurants found nearby'}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {results.map(place => (
              <button
                key={place.placeId}
                onClick={() => !parentLoading && onSelect(place)}
                disabled={parentLoading}
                className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{place.name}</p>
                    <p className="text-xs text-gray-500 truncate mt-0.5">{place.address}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {place.rating && (
                        <span className="text-xs text-amber-600">★ {place.rating}</span>
                      )}
                      {place.priceLevel && (
                        <span className="text-xs text-gray-400">{PRICE_LABELS[place.priceLevel]}</span>
                      )}
                      {place.isOpen === true && (
                        <span className="text-xs text-green-600">Open now</span>
                      )}
                      {place.isOpen === false && (
                        <span className="text-xs text-red-400">Closed now</span>
                      )}
                    </div>
                  </div>
                  <span className="text-gray-300 shrink-0">›</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
